import { useQuery } from '@apollo/client/react';

import {
  BROKERAGE_ACCESS_CHECK_QUERY,
  BROKERAGE_KEYS,
  type BrokerageAccessCheckResult,
  type BrokerageKey,
} from '@/graphql/permissions';
import type { AccessState } from '@/lib/use-resource-access';

export type BrokerageAccess = Record<BrokerageKey, AccessState>;

/** What this login may do on one brokerage, per key, asked once per page load. */
export function useBrokerageAccess(tenantId: string | undefined): {
  access: BrokerageAccess;
  refetch: () => void;
} {
  const { data, loading, error, refetch } = useQuery<BrokerageAccessCheckResult>(
    BROKERAGE_ACCESS_CHECK_QUERY,
    { variables: { tenantId }, skip: !tenantId },
  );

  const stateOf = (key: BrokerageKey): AccessState => {
    if (!tenantId || (loading && !data)) {
      return 'checking';
    }
    if (error || !data) {
      return 'unavailable';
    }
    if (data[`${key}_full`]?.allowed === true) {
      return 'full';
    }
    if (data[`${key}_read`]?.allowed === true) {
      return 'read';
    }
    return data[`${key}_read`]?.status === 'UNAVAILABLE' ? 'unavailable' : 'none';
  };

  const access = Object.fromEntries(
    BROKERAGE_KEYS.map((key) => [key, stateOf(key)]),
  ) as BrokerageAccess;
  return { access, refetch: () => void refetch() };
}
